import { Input } from '../../../../../../../components';
import { createEmptyEntry } from '../utils';
import type { KeyValueEntry } from '../types';
import {
  AddEntryButton,
  DEFAULT_MAX_ITEMS_HEIGHT_CLASSNAME,
  DUPLICATE_KEY_ERROR,
  EMPTY_KEY_ERROR,
  EMPTY_VALUE_ERROR,
  EmptyHint,
  EntriesScrollArea,
  FieldError,
  FieldLabel,
  RemoveEntryButton,
  getDuplicateTrimmedValues,
  removeEntryById,
  updateEntryById,
} from './shared';

type KeyValueField = 'key' | 'value';

type KeyValueListProps = {
  entries: KeyValueEntry[];
  onChange: (entries: KeyValueEntry[]) => void;
  addLabel: string;
  keyPlaceholder?: string;
  valuePlaceholder?: string;
  emptyHint?: string;
  maxItemsHeightClassName?: string;
  lockedKeys?: ReadonlySet<string>;
};

export const isEntryKeyLocked = (
  entry: KeyValueEntry,
  lockedKeys?: ReadonlySet<string>
): boolean => !!lockedKeys && lockedKeys.has(entry.key.trim());

export const getKeyError = (
  trimmedKey: string,
  duplicateKeys: ReadonlySet<string>,
  locked: boolean
): string | undefined => {
  if (locked) return undefined;
  if (!trimmedKey) return EMPTY_KEY_ERROR;
  if (duplicateKeys.has(trimmedKey)) return DUPLICATE_KEY_ERROR;
  return undefined;
};

export const getValueError = (
  value: string,
  locked: boolean
): string | undefined => {
  if (locked) return undefined;
  if (!value.trim()) return EMPTY_VALUE_ERROR;
  return undefined;
};

type EntryRowProps = {
  entry: KeyValueEntry;
  locked: boolean;
  keyError?: string;
  valueError?: string;
  keyPlaceholder: string;
  valuePlaceholder: string;
  onFieldChange: (field: KeyValueField, nextValue: string) => void;
  onRemove: () => void;
};

const EntryRow: React.FC<EntryRowProps> = ({
  entry,
  locked,
  keyError,
  valueError,
  keyPlaceholder,
  valuePlaceholder,
  onFieldChange,
  onRemove,
}) => (
  <div className="flex flex-col gap-y-3 w-full p-4 border border-border rounded-bt bg-gray-50">
    <div className="flex flex-col gap-y-1 w-full">
      <FieldLabel>Key</FieldLabel>
      <div className="flex flex-row items-stretch gap-x-2 w-full">
        <div className="flex-1">
          <Input
            value={entry.key}
            placeholder={keyPlaceholder}
            defaultSizeClass="w-full"
            disabled={locked}
            onChange={next => onFieldChange('key', next)}
          />
        </div>
        <RemoveEntryButton onClick={onRemove} disabled={locked} />
      </div>
      <FieldError message={keyError} />
    </div>

    <div className="flex flex-col gap-y-1 w-full">
      <FieldLabel>Value</FieldLabel>
      <Input
        multiline
        size="small"
        value={entry.value}
        placeholder={valuePlaceholder}
        defaultSizeClass="w-full"
        disabled={locked}
        onChange={next => onFieldChange('value', next)}
      />
      <FieldError message={valueError} />
    </div>
  </div>
);

export const KeyValueList: React.FC<KeyValueListProps> = ({
  entries,
  onChange,
  addLabel,
  keyPlaceholder = 'Key',
  valuePlaceholder = 'Value',
  emptyHint,
  maxItemsHeightClassName = DEFAULT_MAX_ITEMS_HEIGHT_CLASSNAME,
  lockedKeys,
}) => {
  const duplicateKeys = getDuplicateTrimmedValues(
    entries.map(entry => entry.key)
  );
  const hasEntries = entries.length > 0;

  const handleFieldChange = (
    id: string,
    field: KeyValueField,
    nextValue: string
  ) => {
    onChange(updateEntryById(entries, id, { [field]: nextValue }));
  };

  const handleRemove = (id: string) => {
    onChange(removeEntryById(entries, id));
  };

  const handleAdd = () => {
    onChange([...entries, createEmptyEntry()]);
  };

  return (
    <div className="flex flex-col gap-y-3 w-full">
      {!hasEntries && <EmptyHint message={emptyHint} />}

      <EntriesScrollArea
        hasEntries={hasEntries}
        maxHeightClassName={maxItemsHeightClassName}
      >
        {entries.map(entry => {
          const locked = isEntryKeyLocked(entry, lockedKeys);
          const keyError = getKeyError(entry.key.trim(), duplicateKeys, locked);
          const valueError = getValueError(entry.value, locked);

          return (
            <EntryRow
              key={entry.id}
              entry={entry}
              locked={locked}
              keyError={keyError}
              valueError={valueError}
              keyPlaceholder={keyPlaceholder}
              valuePlaceholder={valuePlaceholder}
              onFieldChange={(field, nextValue) =>
                handleFieldChange(entry.id, field, nextValue)
              }
              onRemove={() => handleRemove(entry.id)}
            />
          );
        })}
      </EntriesScrollArea>

      <AddEntryButton label={addLabel} onClick={handleAdd} />
    </div>
  );
};
